import PageLayout from '@/components/layout/PageLayout';
import { Award, Users, Target, Heart } from 'lucide-react';

const About = () => {
  return (
    <PageLayout>
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-primary mb-6" data-testid="about-heading">
              Clarity . Experience . Confidence
            </h1>
          </div>

          {/* Hero Image */}
          <div
            className="h-96 bg-cover bg-center mb-16 border border-border"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1596574027151-2ce81d85af3e?w=1200&h=400&fit=crop')"
            }}
          />

          {/* About Section */}
          <div className="max-w-4xl mx-auto mb-20">
            <p className="font-sans text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
              Forty Chambers is a modern law chambers based in Toronto, Ontario. The Chambers was established in 2023
              because of a need in the community to provide specialised legal consultancy to individuals and businesses.
              As a forward-thinking legal chambers, we are not just lawyers; we are your trusted partner in navigating
              the intricate terrain of modern business and individual legal needs.
            </p>
            <p className="font-sans text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
              In a rapidly evolving legal landscape, businesses and individuals alike require more than just traditional
              legal services. We have embraced all forms of technology that allow Clients throughout the province to
              access our services. We provide strategic guidance, tailored solutions, because we not only understand the
              law but also the unique challenges and opportunities our clients face.
            </p>
            <p className="font-sans text-lg md:text-xl text-muted-foreground leading-relaxed">
              We are dedicated to serving as your in-house counsel for businesses and your go-to source for highly
              specialised advice for individuals. Whether you are a corporation/contractor seeking proactive legal
              counsel or an individual seeking in-depth advice in the following areas: estate planning, corporate law,
              elder law, worker's compensation, as well as notary services.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="bg-secondary p-8 border border-border text-center" data-testid="value-card-clarity">
              <Target className="text-accent mx-auto mb-4" size={48} />
              <h3 className="font-serif text-2xl font-semibold text-primary mb-3">Clarity</h3>
              <p className="font-sans text-base text-muted-foreground leading-relaxed">
                Clear communication and transparent processes ensure you understand every step of your legal journey.
              </p>
            </div>
            <div className="bg-secondary p-8 border border-border text-center" data-testid="value-card-experience">
              <Award className="text-accent mx-auto mb-4" size={48} />
              <h3 className="font-serif text-2xl font-semibold text-primary mb-3">Experience</h3>
              <p className="font-sans text-base text-muted-foreground leading-relaxed">
                Decades of combined legal expertise across multiple practice areas to serve your needs effectively.
              </p>
            </div>
            <div className="bg-secondary p-8 border border-border text-center" data-testid="value-card-confidence">
              <Users className="text-accent mx-auto mb-4" size={48} />
              <h3 className="font-serif text-2xl font-semibold text-primary mb-3">Confidence</h3>
              <p className="font-sans text-base text-muted-foreground leading-relaxed">
                Trust in our ability to deliver results while protecting your interests at every turn.
              </p>
            </div>
          </div>

          {/* Corporate Social Responsibility */}
          <div className="bg-white border border-border p-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-6">
                <Heart className="text-accent" size={48} />
              </div>
              <h2 className="font-serif text-4xl font-bold text-primary text-center mb-8">
                Corporate Social Responsibility
              </h2>
              <p className="font-sans text-lg text-muted-foreground leading-relaxed mb-6">
                At Forty Chambers, we operate a small firm, but our commitment to social responsibility and community
                engagement is as strong as any major corporation. As a small law firm, we understand the profound impact
                we can have on our local community and the broader society. We are dedicated to using our legal expertise
                to give back, make a difference, and support the well-being of those around us.
              </p>

              <div className="space-y-6 mt-8">
                <div>
                  <h3 className="font-serif text-xl font-semibold text-primary mb-2">Pro Bono Legal Services</h3>
                  <p className="font-sans text-base text-muted-foreground leading-relaxed">
                    We recognize that not everyone has access to legal representation. We are committed to providing pro
                    bono legal services to those in need, particularly individuals and community organisations with limited
                    resources. We aim to promote access to justice for all.
                  </p>
                </div>

                <div>
                  <h3 className="font-serif text-xl font-semibold text-primary mb-2">Community Involvement</h3>
                  <p className="font-sans text-base text-muted-foreground leading-relaxed">
                    We actively engage with our local community by participating in volunteer activities, supporting
                    community events, and offering legal advice or workshops to community organisations. We believe in being
                    a positive force in our neighbourhood.
                  </p>
                </div>

                <div>
                  <h3 className="font-serif text-xl font-semibold text-primary mb-2">Environmental Stewardship</h3>
                  <p className="font-sans text-base text-muted-foreground leading-relaxed">
                    Despite our small size, we are dedicated to environmental responsibility. We minimize our ecological
                    footprint by adopting sustainable practices in our home office, such as reducing energy consumption,
                    recycling, and minimising waste.
                  </p>
                </div>

                <div>
                  <h3 className="font-serif text-xl font-semibold text-primary mb-2">Ethical Legal Practices</h3>
                  <p className="font-sans text-base text-muted-foreground leading-relaxed">
                    We uphold the highest ethical standards in our legal practice, ensuring that the well-being and rights
                    of our clients are always prioritised. We are committed to conducting our legal services with integrity,
                    honesty, and professionalism.
                  </p>
                </div>

                <div>
                  <h3 className="font-serif text-xl font-semibold text-primary mb-2">Educational Initiatives</h3>
                  <p className="font-sans text-base text-muted-foreground leading-relaxed">
                    We share our legal knowledge with the community by offering educational resources, workshops, and
                    seminars on various legal topics to empower individuals with the information they need to make informed
                    decisions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default About;